import {Dropdown, MenuProps, Modal, Space} from 'antd';
import {
  Store,
  UpdateStoreInput,
  useDeleteStoreMutation,
  useUpdateStoreMutation
} from 'api/generated';
import {Formik, FormikHelpers} from 'formik';
import React, {useState} from 'react';
import {DeleteOutlined, DownOutlined, EditOutlined} from '@ant-design/icons';
import {StoreForm, validationSchema} from './CreateStoreForm';
import {storesQuery} from 'api/queries';

type Props = {
  store: Store;
};

export const StoresActions: React.FC<Props> = ({store}) => {
  const [isOpen, setOpen] = useState(false);
  const [updateStore] = useUpdateStoreMutation();
  const [deleteStore] = useDeleteStoreMutation({
    updateQuery: {query: storesQuery, variables: {accountId: store.accountId}}
  });
  const handleSubmit = async (
    input: UpdateStoreInput,
    {setSubmitting, resetForm}: FormikHelpers<UpdateStoreInput>
  ) => {
    try {
      await updateStore({
        variables: {
          input: {
            ...input,
            email: input.email || null,
            website: input.website || null,
            phone: input.phone || null,
            address: {
              ...input.address,
              country: input.address?.country || null,
              state: input.address?.state || null,
              city: input.address?.city || null,
              line1: input.address?.line1 || null,
              zip: input.address?.zip || null
            }
          }
        }
      });
      setOpen(false);
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete store',
      content: (
        <span>
          This action will completely delete <b>{store.name}</b>. Are you sure?
        </span>
      ),
      okText: 'Delete',
      okType: 'danger',
      onOk: async () =>
        await deleteStore({
          variables: {id: store.id, accountId: store.accountId},
          mapResultToUpdate: () => store
        })
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'update',
      icon: <EditOutlined />,
      label: 'Update',
      onClick: () => setOpen(true)
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      onClick: handleDelete
    }
  ];

  return (
    <>
      <Dropdown menu={{items: menuItems}} trigger={['click']}>
        <a onClick={(e) => e.preventDefault()}>
          <Space>
            Actions
            <DownOutlined />
          </Space>
        </a>
      </Dropdown>
      <Formik<UpdateStoreInput>
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        initialValues={{...store}}
        enableReinitialize>
        {({handleSubmit, isSubmitting}) => (
          <Modal
            bodyStyle={{paddingBottom: 0}}
            open={isOpen}
            title="Update store"
            okText="Update"
            confirmLoading={isSubmitting}
            onOk={() => handleSubmit()}
            onCancel={() => setOpen(false)}>
            <StoreForm layout="vertical" />
          </Modal>
        )}
      </Formik>
    </>
  );
};
